/// <amd-module name="Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorSelector" />
import * as Reselect from "Reselect";
import { IColumnAndSortOrder, ITestCaseAwareState, ITestCaseItem } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorContracts";
import { getTestCaseItemsComparator } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/TestCaseItemsSortingUtility";
import { getAllColumnValues, getWITColumnFields } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/DefineColumnOptionUtils";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { IUserIdentity } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import { getDefineColumnOptionFields } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as TestHubResources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { ITestHubListBoxItemData } from "Test/TestPlansHubRefresh/TestPlansHubRefreshFilter/TestPlansHubRefreshFilterComponent.Types";
import { TestCase, WorkItemDetails } from "TestPlanCommon/Contracts/TestPlan";
import { ObservableArray } from "VSS/Core/Observable";
import { IListBoxItem } from "VSSUI/ListBox";
import { IFilterState } from "VSSUI/Utilities/Filter";

export const getTestCasesMapFromState = (state: ITestCaseAwareState): { [id: number]: TestCase } => state.authorState.testCases;

const _getTestCaseName = (testCase: TestCase): string => testCase.workItem.name;
const _getTestCaseId = (testCase: TestCase): number => testCase.workItem.id;

export const getColumnAndSortOrder = (state: ITestCaseAwareState): IColumnAndSortOrder | undefined => {
    if (state && state.authorState) {
        return state.authorState.columnAndSortOrder;
    }
};

export const isAnyAuthorFilterValueSet = (filterState: IFilterState | undefined) => {
    let isFilterValueSet = false;

    const authorFilterItemKeys = [
        TestHubRefreshConstants.AUTHOR_TEXT_FILTER_ITEM_KEY,
        TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY,
        TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY
    ];

    // Check if any of the author filter item keys are set
    if (filterState) {
        for (const key of authorFilterItemKeys) {
            const filterItemState = filterState[key];
            if (filterItemState && filterItemState.value && filterItemState.value.length) {
                isFilterValueSet = true;
                break;
            }
        }
    }

    return isFilterValueSet;
};

export const getFilteredTestCases = (state: ITestCaseAwareState): number[] | undefined => {
    if (state && state.authorFilterState) {
        return state.authorFilterState.filteredTestCaseIds;
    }
};

export const getTestCasesFromState = (state: ITestCaseAwareState): TestCase[] => {
    let testCases: TestCase[] = [];
    if (state.authorState && state.authorState.testCases) {
        const allKeys: number[] = Object.keys(state.authorState.testCases).map(key => parseInt(key));
        testCases = allKeys.map(key => state.authorState.testCases[key]);
    }
    return testCases;
};

export const getTestCaseItemsFromState = (state: ITestCaseAwareState, fetchTags?: boolean): ITestCaseItem[] | undefined => {
    if (state.authorState && state.authorState.testCases) {
        const testCases: TestCase[] = getTestCasesFromState(state);
        return getTestCaseItemsFromTestCase(testCases, fetchTags);
    }

    return undefined;
};

export const getTestCasesCount = (state: ITestCaseAwareState): number | undefined => {
    if (state.authorState.testCases) {
        return Object.keys(state.authorState.testCases).length;
    }
    return undefined;
};

export function getTestCaseItemsFromTestCase(testCases: TestCase[], fetchTags?: boolean): ITestCaseItem[] {
    const testCasesForView: ITestCaseItem[] = [];

    if (testCases) {
        for (let i = 0; i < testCases.length; i++) {
            testCasesForView.push({
                id: _getTestCaseId(testCases[i]),
                name: _getTestCaseName(testCases[i]),
                assignedTo: _getAssignedTo(testCases[i]),
                order: _getTestCaseOrder(testCases[i]),
                state: _getTestCaseWorkItemField(testCases[i], TestHubRefreshConstants.WorkItemField_State),
                pointAssignments: testCases[i].pointAssignments
            });
            if(fetchTags)
            {
                testCasesForView[i].columnOptionValues = getAllColumnValues([TestHubRefreshConstants.WorkItemField_Tags], testCases[i]);
            }
        }
    }

    return testCasesForView;
}

function _getTestCaseWorkItemField(testCase: TestCase, fieldName: string): string {
    let field = "";
    let testCaseWorkItem: WorkItemDetails;

    if (testCase && testCase.workItem && testCase.workItem.workItemFields) {
        testCaseWorkItem = testCase.workItem;
        const workItemAssignedToField = testCaseWorkItem.workItemFields.find(item => item.hasOwnProperty(fieldName));
        if (workItemAssignedToField) {
            field = workItemAssignedToField[fieldName];
        }
    }

    return field;
}

function _getAssignedTo(testCase: TestCase): IUserIdentity {
    let testCaseWorkItem: WorkItemDetails;
    const userIdentity: IUserIdentity = {
        id: "00000000-0000-0000-0000-000000000000",
        displayName: TestHubResources.UnassignedString
    };

    if (testCase && testCase.workItem && testCase.workItem.workItemFields) {
        testCaseWorkItem = testCase.workItem;
        const workItemAssignedToField = testCaseWorkItem.workItemFields.find(item =>
            item.hasOwnProperty(TestHubRefreshConstants.WorkItemField_AssignedTo)
        );
        if (workItemAssignedToField) {
            const workItemIdentity = workItemAssignedToField[TestHubRefreshConstants.WorkItemField_AssignedTo];

            if (workItemIdentity && workItemIdentity.identityRef) {
                userIdentity.id = workItemIdentity.identityRef.id;

                userIdentity.displayName = workItemIdentity.identityRef.displayName
                    ? workItemIdentity.identityRef.displayName
                    : workItemIdentity.identityRef.uniqueName;
            }
        }
    }

    return userIdentity;
}

function _getTestCaseOrder(testCase: TestCase): number {
    let order = 1;
    // the api does not return order field for the first test case so return 1 in that case, for all others return order + 1 (since the 2nd test case has order = 1 in api response)
    if (testCase && testCase.order) {
        order = testCase.order + 1;
    }

    return order;
}

export const getWITColumnOptionFields = (state: ITestCaseAwareState): string | undefined => {
    const witFields = [TestHubRefreshConstants.WorkItemField_Ttile];
    if (state && state.testHubRefreshState && state.testHubRefreshState.defineColumnOptionFields) {
        witFields.push(...getWITColumnFields(state.testHubRefreshState.defineColumnOptionFields));
    }
    return witFields.join(",");
};

export const getWITColumnOptionFieldsArray = (state: ITestCaseAwareState): string[] | undefined => {
    let witFields: string[] = [];
    if (state && state.testHubRefreshState && state.testHubRefreshState.defineColumnOptionFields) {
        witFields = getWITColumnFields(state.testHubRefreshState.defineColumnOptionFields);
    }
    return witFields;
};

export const getTestCasesToRender = Reselect.createSelector(
    getTestCasesMapFromState,
    getTestCaseItemsFromState,
    getFilteredTestCases,
    getColumnAndSortOrder,
    getDefineColumnOptionFields,
    (
        allTestCasesMap: { [id: number]: TestCase },
        testCaseItems: ITestCaseItem[],
        filteredTestCases: number[] | undefined,
        columnAndSortOrder: IColumnAndSortOrder,
        allColumnOptionFields: any[]
    ): ITestCaseItem[] => {
        const arrayToReturn: ITestCaseItem[] = [];
        const tempFilteredTestCases: TestCase[] = [];
        let tempFilteredTestCaseItems: ITestCaseItem[] = [];
        if (!filteredTestCases) {
            if (testCaseItems) {
                if (allTestCasesMap && allColumnOptionFields) {
                    for (const testCase of testCaseItems) {
                        testCase.columnOptionValues = getAllColumnValues(allColumnOptionFields, allTestCasesMap[testCase.id]);
                    }
                }
                testCaseItems = testCaseItems.sort(getTestCaseItemsComparator(columnAndSortOrder.columnId, columnAndSortOrder.sortOrder));
            }
            return testCaseItems;
        }

        if (filteredTestCases && filteredTestCases.length === 0) {
            return arrayToReturn;
        }

        for (let i = 0; i < filteredTestCases.length; i++) {
            if (allTestCasesMap[filteredTestCases[i]]) {
                tempFilteredTestCases.push(allTestCasesMap[filteredTestCases[i]]);
            }
        }

        if (tempFilteredTestCases) {
            tempFilteredTestCaseItems = getTestCaseItemsFromTestCase(tempFilteredTestCases);
            if (tempFilteredTestCaseItems) {
                if (allTestCasesMap && allColumnOptionFields) {
                    for (const testCase of tempFilteredTestCaseItems) {
                        testCase.columnOptionValues = getAllColumnValues(allColumnOptionFields, allTestCasesMap[testCase.id]);
                    }
                }

                tempFilteredTestCaseItems = tempFilteredTestCaseItems.sort(
                    getTestCaseItemsComparator(columnAndSortOrder.columnId, columnAndSortOrder.sortOrder)
                );
            }
        }
        return tempFilteredTestCaseItems;
    }
);

/**
 * getUniqueFilterValuesToRender: This selector loops through all the items and returns an object containing unique values for each of the Author filter (AssignedTo and State)
 */
export const getUniqueFilterValuesToRender = Reselect.createSelector(getTestCaseItemsFromState, (allTestCaseItems: ITestCaseItem[]) => {
    const assignedToValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();
    const stateValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();

    if (allTestCaseItems && allTestCaseItems.length > 0) {
        const assignedToMap: Map<string, number> = new Map<string, number>();
        const stateToIdMap: Map<string, number> = new Map<string, number>();

        for (const testCaseItem of allTestCaseItems) {
            // process assignedTo field
            if (!assignedToMap.has(testCaseItem.assignedTo.id)) {
                assignedToMap.set(testCaseItem.assignedTo.id, testCaseItem.id);

                // for assignedTo field, use same values for id and text, need to store VSID in id later for suite count
                // only the data is passed to the currentFilterState stored in state. The key is used for filter matches and the value is used for other processing.
                const item: IListBoxItem<ITestHubListBoxItemData> = {
                    id: testCaseItem.assignedTo.displayName,
                    text: testCaseItem.assignedTo.displayName,
                    data: {
                        key: testCaseItem.assignedTo.displayName,
                        value: testCaseItem.assignedTo
                    }
                };
                assignedToValues.push(item);
            }

            // process state field
            if (!stateToIdMap.has(testCaseItem.state)) {
                stateToIdMap.set(testCaseItem.state, testCaseItem.id);

                // for state filter assign both id and text to same value, since state doesn't really have an id.
                const item: IListBoxItem<ITestHubListBoxItemData> = {
                    id: testCaseItem.state,
                    text: testCaseItem.state,
                    data: {
                        key: testCaseItem.state,
                        value: testCaseItem.state
                    }
                };
                stateValues.push(item);
            }
        }
    }

    const wrapperObject: { [key: string]: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> } = {};
    wrapperObject[TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY] = assignedToValues;
    wrapperObject[TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY] = stateValues;

    return wrapperObject;
});
